import React, { useCallback } from 'react'

import Box from 'components_new/atoms/Box'
import Text from 'components_new/atoms/Text'

import { useTranslation } from 'language/translator'

import MetricPreview from 'components_new/organisms/MetricPreview'

import PreviewDashboardWidget from './PreviewDashboardWidget'
import VariantStep from './VariantStep'
import RollingSumStep from './RollingSumStep'
import DistributionStep from './DistributionStep'
import SlicesStep from './SlicesStep'

import { ISelectMetric, TMetricSettings } from 'types/GlobalMetric'
import { TCoreWidgetSettingsLoaded, WidgetType } from 'types/GlobalWidget'

import { type Step, TMetricDialogVariant, getStepMeta } from '../lib'
import SelectMetric from './SelectMetric'

interface Props {
  category: string | null
  currentStep: Step
  handleAddOther?: (
    type: WidgetType.TEXT | WidgetType.COMMENT | WidgetType.IMAGE
  ) => void
  metric: ISelectMetric | null
  search: string | null
  setSelectedMetric: (metric: string, defaultVariant: string | null) => void
  settings: TMetricSettings
  setSettings: React.Dispatch<React.SetStateAction<TMetricSettings>>
  subcategory: string | null
  widget?: TCoreWidgetSettingsLoaded
  tab: 'metrics' | 'other'
  variant: TMetricDialogVariant
}

const Content = (props: Props) => {
  const { t } = useTranslation()

  const {
    category,
    currentStep,
    handleAddOther,
    metric,
    search,
    setSelectedMetric,
    settings,
    setSettings,
    variant,
    subcategory,
    widget,
    tab
  } = props

  const setSettingsField = useCallback(
    <K extends keyof TMetricSettings>(key: K, value: TMetricSettings[K]) => {
      setSettings((prev) => ({
        ...prev,
        [key]: value
      }))
    },
    [setSettings]
  )

  if (!metric) {
    return (
      <SelectMetric
        category={category}
        handleAddOther={handleAddOther}
        search={search}
        setSelectedMetric={setSelectedMetric}
        subcategory={subcategory}
        widget={widget}
        tab={tab}
      />
    )
  }

  const { title, description } = getStepMeta(currentStep, t)

  return (
    <Box sx={{ flex: 1, p: 6 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          mb: 6
        }}
      >
        <Box sx={{ maxWidth: 'title' }}>
          <Box sx={{ mb: 6 }}>
            <Text align="center" gutterBottom={true} variant="h5">
              {title}
            </Text>
            <Text align="center" color="text.secondary" variant="body1">
              {description}
            </Text>
          </Box>
          {variant === 'metrics' ? (
            <MetricPreview id={metric.id} settings={settings} />
          ) : (
            <PreviewDashboardWidget
              id={metric.id}
              settings={settings}
              variant={variant}
            />
          )}
        </Box>
      </Box>

      <VariantStep
        step={currentStep}
        metric={metric}
        variant={settings.variant_kpi_template_id}
        setVariant={(value) =>
          setSettingsField('variant_kpi_template_id', value)
        }
      />
      <RollingSumStep
        step={currentStep}
        metric={metric}
        rollingSum={settings.rolling_sum}
        setRollingSum={(rsum) => setSettingsField('rolling_sum', rsum)}
      />
      <DistributionStep
        step={currentStep}
        metric={metric}
        distribution={settings.distribution}
        setDistribution={(dist) => setSettingsField('distribution', dist)}
      />
      <SlicesStep
        step={currentStep}
        metric={metric}
        slices={settings.slices}
        setSlices={(slices) => setSettingsField('slices', slices)}
      />
    </Box>
  )
}

export default Content
